// Mock data for MG-AI - Complete AI Solutions Provider

export const agenticSpecialization = {
  title: "Our Agentic AI & RAG Specialization",
  subtitle: "The Most Efficient, Secure, and Governed AI Agents",
  description: "We specialize in building Agentic AI systems and RAG applications that are not just powerful—they're the most efficient, secure, ethical, and fully governed from data ingestion to monitoring.",
  highlights: [
    {
      icon: "Bot",
      title: "Most Efficient Agents",
      description: "We create AI agents that maximize performance while minimizing resource consumption. Every decision, every action is optimized for efficiency and effectiveness."
    },
    {
      icon: "Shield",
      title: "Privacy & Security First",
      description: "Your data stays private, always. End-to-end encryption, secure data handling, and strict privacy protocols at every layer of the system."
    },
    {
      icon: "Scale",
      title: "Ethical & Compliant",
      description: "Compliant with global regulations (GDPR, CCPA, SOC 2, ISO 27001) and your company's internal rules. Our agents operate ethically, transparently, and within all legal boundaries."
    },
    {
      icon: "Target",
      title: "Fully Governed Components",
      description: "Every single component is governed—from data ingestion and processing to decision-making and monitoring. Complete visibility, control, and audit trails for every agent action."
    }
  ],
  governance: {
    title: "Complete Agent Governance",
    points: [
      "Data Ingestion: Validated, sanitized, and logged",
      "Processing: Auditable transformations and operations",
      "Decision-Making: Explainable AI with clear reasoning trails",
      "Action Execution: Controlled, monitored, and reversible",
      "Monitoring: Real-time observability and alerting",
      "Compliance: Automated checks at every step"
    ]
  }
};

export const mission = {
  title: "AI Solutions. Any Problem.",
  subtitle: "What We Build",
  vision: "From basic algorithms to advanced robotics and autonomous systems—we build perfect AI solutions. No problem too complex, no challenge too simple. Perfect solutions with MG-AI across the entire AI spectrum.",
  tagline: "Perfect Solutions with MG-AI",
  description: "Complete AI expertise from basic machine learning to cutting-edge autonomous systems.",
  focus: [
    {
      icon: "TrendingUp",
      title: "Predictive AI",
      points: [
        "Sales forecasting and demand prediction",
        "Simple regression to complex time-series models",
        "Pattern recognition and anomaly detection",
        "Risk assessment and fraud detection",
        "Customer behavior prediction"
      ]
    },
    {
      icon: "Bot",
      title: "Agentic AI & Automation",
      points: [
        "Single and multi-agent AI systems",
        "Autonomous decision-making agents",
        "Intelligent workflow automation",
        "RAG applications and knowledge systems",
        "MCP protocol integration and orchestration"
      ]
    },
    {
      icon: "Settings",
      title: "Computer Vision",
      points: [
        "Image classification and object detection",
        "Self-driving car vision systems",
        "Medical image analysis",
        "Real-time video processing",
        "Facial recognition and biometrics"
      ]
    },
    {
      icon: "Sparkles",
      title: "Robotics & Autonomous Systems",
      points: [
        "Autonomous robot movement and navigation",
        "Robotic process automation (RPA)",
        "Industrial automation systems",
        "Drone and vehicle autonomy",
        "Reinforcement learning for robotics"
      ]
    }
  ]
};

export const capabilities = {
  title: "Our Capabilities",
  subtitle: "The Complete AI Spectrum",
  description: "We master every layer of artificial intelligence—from foundational algorithms to bleeding-edge autonomous systems. Whatever your challenge, we have the expertise.",
  areas: [
    {
      icon: "Database",
      title: "Machine Learning Foundation",
      description: "Linear regression, logistic regression, decision trees, random forests, gradient boosting, SVMs, clustering, dimensionality reduction—all the classics, perfected."
    },
    {
      icon: "TrendingUp",
      title: "Deep Learning & Neural Networks",
      description: "CNNs, RNNs, LSTMs, Transformers, GANs. Computer vision, NLP, speech recognition, time-series forecasting—we build custom architectures for your specific needs."
    },
    {
      icon: "Bot",
      title: "Agentic AI & LLMs",
      description: "GPT-based agents, autonomous reasoning systems, multi-agent orchestration, RAG applications, tool-using AI, MCP integration—the future of AI, deployed today."
    },
    {
      icon: "Zap",
      title: "Robotics & Real-World AI",
      description: "Autonomous navigation, robotic manipulation, self-driving systems, reinforcement learning, real-time decision-making—AI that moves, acts, and adapts in the physical world."
    }
  ]
};

export const services = [
  {
    id: 1,
    title: "Predictive Analytics & Forecasting",
    icon: "TrendingUp",
    category: "Prediction",
    description: "From simple regression to advanced neural forecasting—predict sales, demand, customer behavior, market trends, and business outcomes with precision.",
    features: [
      "Sales and revenue forecasting",
      "Demand prediction and inventory optimization",
      "Customer churn prediction",
      "Time-series analysis and forecasting",
      "Risk assessment and fraud detection"
    ]
  },
  {
    id: 2,
    title: "Computer Vision Systems",
    icon: "Eye",
    category: "Vision",
    description: "Image classification, object detection, video analysis, facial recognition—we build vision systems that see, understand, and act on visual data.",
    features: [
      "Image classification and object detection",
      "Medical image analysis",
      "Self-driving car vision systems",
      "Real-time video processing",
      "Quality control and defect detection"
    ]
  },
  {
    id: 3,
    title: "Autonomous Robotics & Systems",
    icon: "Factory",
    category: "Robotics",
    description: "Make robots move autonomously. Navigation, manipulation, decision-making—we build AI that controls physical systems in the real world.",
    features: [
      "Autonomous robot navigation and movement",
      "Self-driving vehicles and drones",
      "Robotic process automation (RPA)",
      "Industrial automation systems",
      "Reinforcement learning for robotics"
    ]
  },
  {
    id: 4,
    title: "Intelligent Agents & AI Systems",
    icon: "Bot",
    category: "Agents",
    description: "Agentic AI, multi-agent orchestration, autonomous decision-making—AI that reasons, plans, and executes complex tasks without human intervention.",
    features: [
      "Single and multi-agent systems",
      "Autonomous decision-making AI",
      "RAG applications and knowledge systems",
      "Tool-using and API-calling agents",
      "MCP protocol integration"
    ]
  },
  {
    id: 5,
    title: "Natural Language Processing",
    icon: "MessageCircle",
    category: "NLP",
    description: "Text analysis, sentiment detection, chatbots, language understanding—AI that reads, understands, and generates human language.",
    features: [
      "Sentiment analysis and opinion mining",
      "Named entity recognition",
      "Text classification and summarization",
      "Chatbots and conversational AI",
      "Machine translation"
    ]
  },
  {
    id: 6,
    title: "Pattern Recognition & Anomaly Detection",
    icon: "Target",
    category: "Detection",
    description: "Find patterns in data, detect anomalies, predict the next move—AI that discovers hidden insights and identifies what doesn't belong.",
    features: [
      "Pattern recognition in complex data",
      "Anomaly and outlier detection",
      "Fraud detection systems",
      "Network intrusion detection",
      "Predictive maintenance"
    ]
  }
];

export const preBuiltRAGApps = [
  {
    id: 1,
    name: "Legal Discovery Assistant",
    domain: "Legal",
    icon: "Scale",
    description: "Instantly search and analyze thousands of legal documents, contracts, and case law.",
    capabilities: [
      "Contract analysis & comparison",
      "Case law research",
      "Compliance checking",
      "Document summarization"
    ],
    metrics: "85% faster document review"
  },
  {
    id: 2,
    name: "Healthcare Knowledge Base",
    domain: "Healthcare",
    icon: "Stethoscope",
    description: "Medical literature search, patient record analysis, and clinical decision support.",
    capabilities: [
      "Medical literature search",
      "Patient history analysis",
      "Treatment recommendations",
      "Drug interaction checks"
    ],
    metrics: "70% reduction in diagnosis time"
  },
  {
    id: 3,
    name: "Financial Intelligence Hub",
    domain: "Finance",
    icon: "TrendingUp",
    description: "Real-time market analysis, risk assessment, and regulatory compliance monitoring.",
    capabilities: [
      "Market trend analysis",
      "Risk assessment reports",
      "Regulatory compliance checks",
      "Financial document Q&A"
    ],
    metrics: "90% faster compliance reporting"
  },
  {
    id: 4,
    name: "HR Knowledge Assistant",
    domain: "Human Resources",
    icon: "Users",
    description: "Employee handbook navigation, policy Q&A, and onboarding automation.",
    capabilities: [
      "Policy & procedure Q&A",
      "Onboarding automation",
      "Benefits information",
      "Training recommendations"
    ],
    metrics: "60% reduction in HR queries"
  },
  {
    id: 5,
    name: "Manufacturing Intelligence",
    domain: "Manufacturing",
    icon: "Factory",
    description: "Equipment manuals, safety protocols, and production optimization insights.",
    capabilities: [
      "Equipment troubleshooting",
      "Safety protocol lookup",
      "Maintenance scheduling",
      "Quality control insights"
    ],
    metrics: "40% less equipment downtime"
  },
  {
    id: 6,
    name: "Customer Support Copilot",
    domain: "Customer Support",
    icon: "Headphones",
    description: "Instant answers from product docs, KB articles, and support ticket history.",
    capabilities: [
      "Product documentation Q&A",
      "Ticket history analysis",
      "Solution recommendations",
      "Automated responses"
    ],
    metrics: "10× faster response times"
  }
];

export const useCases = [
  {
    id: 1,
    title: "Automated Legal Contract Review",
    industry: "Legal Services",
    challenge: "Law firm spending 200+ hours/month manually reviewing contracts",
    solution: "Deployed Agentic AI system with RAG-powered contract analysis",
    outcome: "85% reduction in review time, 99% accuracy in clause identification",
    technologies: ["Multi-agent orchestration", "Legal document RAG", "Clause extraction"]
  },
  {
    id: 2,
    title: "Clinical Decision Support System",
    industry: "Healthcare",
    challenge: "Hospital needed faster access to medical literature for treatment decisions",
    solution: "RAG system integrated with PubMed, clinical guidelines, and patient records",
    outcome: "70% faster diagnosis, 30% improvement in treatment accuracy",
    technologies: ["Medical knowledge RAG", "Multi-modal processing", "HIPAA compliance"]
  },
  {
    id: 3,
    title: "Financial Fraud Detection Agent",
    industry: "Banking",
    challenge: "Bank processing millions of transactions, missing fraud patterns",
    solution: "Agentic AI monitoring transactions, learning fraud patterns, auto-flagging suspicious activity",
    outcome: "95% fraud detection rate, $2M+ saved annually",
    technologies: ["Real-time agents", "Anomaly detection", "Automated response"]
  },
  {
    id: 4,
    title: "Enterprise Knowledge Search",
    industry: "Technology",
    challenge: "Tech company with scattered documentation across 15+ platforms",
    solution: "Unified RAG system indexing all knowledge sources with semantic search",
    outcome: "10× faster information retrieval, 50% reduction in duplicate work",
    technologies: ["Cross-platform RAG", "Semantic search", "Real-time indexing"]
  },
  {
    id: 5,
    title: "Supply Chain Optimization Agent",
    industry: "Logistics",
    challenge: "Logistics company struggling with route optimization and inventory management",
    solution: "Autonomous agent analyzing supply chain data, optimizing routes and inventory",
    outcome: "25% cost reduction, 40% improvement in delivery times",
    technologies: ["Planning agents", "Predictive analytics", "Tool integration"]
  },
  {
    id: 6,
    title: "Regulatory Compliance Monitor",
    industry: "Financial Services",
    challenge: "Investment firm needed real-time compliance monitoring across regulations",
    solution: "AI agent continuously monitoring regulations, auto-generating compliance reports",
    outcome: "100% compliance rate, 80% reduction in manual compliance work",
    technologies: ["Regulatory RAG", "Automated reporting", "Change detection"]
  },
  {
    id: 7,
    title: "Customer Service Automation",
    industry: "E-commerce",
    challenge: "E-commerce platform handling 10K+ customer queries daily",
    solution: "Multi-agent system with RAG for product info, orders, and support history",
    outcome: "80% automation rate, 95% customer satisfaction",
    technologies: ["Multi-agent support", "Product catalog RAG", "Sentiment analysis"]
  },
  {
    id: 8,
    title: "Research & Development Accelerator",
    industry: "Pharmaceutical",
    challenge: "Pharma company needed faster literature review for drug development",
    solution: "RAG system analyzing millions of research papers, clinical trials, and patents",
    outcome: "60% faster research cycles, 3× more drug candidates identified",
    technologies: ["Scientific literature RAG", "Citation analysis", "Trend detection"]
  }
];

export const process = [
  {
    step: 1,
    title: "Discover",
    description: "We analyze your business needs, identify AI opportunities, and define success metrics.",
    activities: ["Use case workshops", "Data assessment", "ROI modeling", "Feasibility analysis"]
  },
  {
    step: 2,
    title: "Design",
    description: "Architect the solution with your team, design data flows, and plan integration.",
    activities: ["System architecture", "Data pipeline design", "Security framework", "Integration planning"]
  },
  {
    step: 3,
    title: "Build",
    description: "Develop custom agents and RAG systems tailored to your requirements.",
    activities: ["Agent development", "Knowledge base creation", "Model fine-tuning", "Testing & validation"]
  },
  {
    step: 4,
    title: "Deploy",
    description: "Launch in your secure environment with full compliance and monitoring.",
    activities: ["Cloud deployment", "Security configuration", "Team training", "Go-live support"]
  },
  {
    step: 5,
    title: "Optimize",
    description: "Continuously improve performance with feedback loops and model updates.",
    activities: ["Performance monitoring", "User feedback integration", "Model retraining", "Feature enhancements"]
  }
];

export const pricingPlans = [
  {
    id: 1,
    name: "Starter",
    price: "Custom Quote",
    description: "Perfect for teams exploring Agentic AI and RAG",
    features: [
      "1 Pre-built RAG application",
      "Up to 10K queries/month",
      "Standard support (48h response)",
      "Basic security & compliance",
      "30-day implementation"
    ],
    recommended: false,
    tag: "For Small Teams"
  },
  {
    id: 2,
    name: "Enterprise",
    price: "Custom Quote",
    description: "Comprehensive AI solutions for growing organizations",
    features: [
      "Custom Agentic AI + RAG systems",
      "Unlimited queries",
      "Priority support (4h response)",
      "Full GDPR, SOC 2, ISO compliance",
      "Dedicated AI engineer",
      "Private cloud deployment",
      "Advanced monitoring & analytics",
      "90-day implementation"
    ],
    recommended: true,
    tag: "Most Popular"
  },
  {
    id: 3,
    name: "Transformation",
    price: "Contact Us",
    description: "Complete AI transformation with training and ongoing innovation",
    features: [
      "Multi-agent ecosystems",
      "Organization-wide RAG platform",
      "24/7 managed operations",
      "White-glove support",
      "Executive AI strategy consulting",
      "Custom compliance frameworks",
      "Team training & certification",
      "Continuous innovation partnership",
      "Flexible timeline"
    ],
    recommended: false,
    tag: "Full Partnership"
  }
];

export const companyInfo = {
  name: "MG-AI",
  tagline: "Perfect Solutions with MG-AI",
  mission: "We build perfect AI solutions across the entire spectrum—from basic algorithms to advanced robotics and autonomous systems. Any problem, expert execution.",
  stats: [
    { label: "AI Solutions", value: "Any Scale" },
    { label: "Regression to Robotics", value: "Complete Spectrum" },
    { label: "Execution", value: "Perfect" },
    { label: "Expertise", value: "All AI Domains" }
  ],
  compliance: [
    { name: "GDPR", status: "Certified" },
    { name: "SOC 2 Type II", status: "Certified" },
    { name: "ISO 27001", status: "Certified" },
    { name: "Data Security", status: "Encrypted" }
  ]
};

export const contactInfo = {
  email: "mgai-enquiries@mightgai.in",
  phone: "+91 6388934846",
  whatsapp: "+916388934846"
};
